import classNames from "classnames";
import {useState} from "react";
import {useFieldArray, useFormContext} from "react-hook-form";
import {FiPlus} from "react-icons/fi";

import Button from "@/components/button/Button.tsx";
import {Icon} from "@/components/icon/Icon.tsx";
import {TooltipIconRA} from "@/components/overlay/TooltipIconRA.tsx";
import {IncidentState, IncidentType} from "@/lib/types/statuspages.ts";
import {UpdateField} from "@/pages/incidents/components/incident-edit/components/updates/UpdateField.tsx";
import {IncidentEditFormData} from "@/pages/incidents/components/incident-edit/IncidentForm.types.ts";

import styles from "@/components/form/FormLabel.module.scss";

export const IncidentUpdatesForm = () => {
  const {setValue, watch, control} = useFormContext<IncidentEditFormData>();
  const {
    fields: updateFields,
    insert: updateInsert,
    remove: updateRemove,
  } = useFieldArray({
    control,
    name: "updates",
    keyName: "key",
  });
  const historyUpdates = updateFields.filter((u) => u.id);
  const shouldDisplayNewUpdateButton = updateFields.filter((u) => !u.id).length === 0;
  const hasHistoryUpdates = historyUpdates.length > 0;
  const [openUpdateIds, setOpenUpdateIds] = useState<number[]>([]);
  const setUpdatesUnderEditing = (updateId: number | undefined, isEditing: boolean) => {
    updateId = updateId || 0;
    if (!isEditing && openUpdateIds.includes(updateId)) {
      setOpenUpdateIds(openUpdateIds.filter((u) => u !== updateId));
    } else if (isEditing && !openUpdateIds.includes(updateId)) {
      setOpenUpdateIds([updateId, ...openUpdateIds]);
    }
  };
  const incidentType = watch("incident_type");
  const isScheduledMaintenance = incidentType === IncidentType.SCHEDULED_MAINTENANCE;
  return (
    <div className={"d-flex flex-column gap-3"}>
      <div className="d-flex flex-row justify-content-between align-items-center">
        <div className="pulseTextBodySmallBold">
          Update
          <TooltipIconRA
            className={classNames("ms-2", styles.tooltip)}
            title={`Add maintenance updates throughout the ${
              incidentType === IncidentType.INCIDENT ? "duration of an incident" : "scheduled window"
            }, mark as resolved to display as past maintenance event.`}
            iconProps={{
              display: "inline",
              removeDivDimensions: true,
            }}
          />
        </div>
        <div>
          {shouldDisplayNewUpdateButton && (
            <Button
              variant={"secondary"}
              className="float-end"
              onClick={() => {
                updateInsert(0, {
                  description: "",
                  incident_state: isScheduledMaintenance ? IncidentState.MAINTENANCE : IncidentState.INVESTIGATION,
                  confirmed: false,
                });
              }}
            >
              <Icon icon={FiPlus} /> New Update
            </Button>
          )}
        </div>
      </div>
      {updateFields.map((u, idx) =>
        !u.id ? (
          <UpdateField
            variant="standalone"
            updateRequired={updateFields.length <= 1}
            id={u.id}
            key={`updates-${u.id || 0}`}
            name={`updates.${idx}`}
            descriptionHTML={u.description_html}
            onEditStateChange={(updateState) => setUpdatesUnderEditing(u.id, updateState)}
            onDelete={() => {
              updateRemove(idx);
              setUpdatesUnderEditing(u.id, false);
              // if an update is added and then removed without saving - need to reset
              // the notify subscribers field
              setValue("notify_subscribers", false);
              setValue("resolve_components", false);
            }}
            // serverErrors={serverErrors}
          />
        ) : (
          false
        )
      )}
      {hasHistoryUpdates && (
        <div className={"d-flex flex-column gap-3"}>
          <div className="pulseTextBodySmallBold">
            History {historyUpdates.length > 0 ? `(${historyUpdates.length})` : ""}
          </div>
          <div>
            {updateFields.map((u, idx) =>
              u.id ? (
                <UpdateField
                  variant="history"
                  updateRequired={updateFields.length <= 1}
                  id={u.id}
                  key={`updates-${u.id || 0}`}
                  name={`updates.${idx}`}
                  descriptionHTML={u.description_html}
                  updatedAt={u.updatedAt}
                  onEditStateChange={(updateState) => setUpdatesUnderEditing(u.id, updateState)}
                  onDelete={() => {
                    updateRemove(idx);
                    setUpdatesUnderEditing(u.id, false);
                  }}
                />
              ) : (
                false
              )
            )}
          </div>
        </div>
      )}
    </div>
  );
};
